/**
 * Effectifs adhérents et produit moyen.
 *
 * Règles imposées (spécification § 5) :
 *   — est payeur l'adhérent dont la cotisation de l'exercice porte l'un des
 *     trois états « Payée », « Payée partiellement » ou « Payé - Doc.
 *     comptables en attente » ;
 *   — les doublons de structure sur un même exercice comptent pour un ;
 *   — le produit moyen est le rapport des cotisations acquittées au nombre de
 *     payeurs de l'exercice ;
 *   — les cotisations solidaires 2020 et 2021 sont exclues et signalées.
 *
 * L'API Directus ne propose pas d'agrégat `countDistinct` : le dénombrement se
 * fait ici, après récupération des lignes, en écartant les doublons.
 */
import { COTISATION_SOLIDAIRE, ETATS_PAYEURS } from './configuration.js';
/** Une cotisation vaut-elle « adhérent payeur » ? */
export function estPayeur(ligne) {
    return ETATS_PAYEURS.includes(ligne.etat);
}
/**
 * Clé d'unicité d'un cotisant sur un exercice. La structure prime ; à défaut
 * on retombe sur l'individu. Une ligne sans structure ni individu ne peut pas
 * être dédoublonnée : elle reçoit une clé propre et compte pour une unité.
 */
export function cleCotisant(ligne, rang) {
    if (ligne.structure !== null)
        return `s:${ligne.structure}`;
    if (ligne.individu !== null)
        return `i:${ligne.individu}`;
    return `x:${rang}`;
}
/**
 * Nombre d'adhérents payeurs distincts et nombre de doublons écartés.
 * Exposé séparément pour être testé sur un jeu contenant volontairement des
 * doublons — ce détail a déjà produit des erreurs de comptage.
 */
export function compterPayeursDistincts(lignes) {
    const cles = new Set();
    let doublons = 0;
    lignes.forEach((l, i) => {
        if (!estPayeur(l))
            return;
        const c = cleCotisant(l, i);
        if (cles.has(c))
            doublons += 1;
        else
            cles.add(c);
    });
    return { payeurs: cles.size, doublonsEcartes: doublons };
}
/**
 * Somme de montants en euros, calculée en millionièmes entiers pour éviter
 * l'accumulation d'erreur des flottants sur plusieurs centaines de lignes.
 */
export function sommeExacte(montants) {
    const micro = montants.reduce((s, m) => s + Math.round((m ?? 0) * 1e6), 0);
    return micro / 1e6;
}
/** Série complète d'un exercice : payeurs, cotisations, produit moyen, recouvrement. */
export function serieExercice(annee, lignesExercice) {
    const payeuses = lignesExercice.filter(estPayeur);
    const { payeurs, doublonsEcartes } = compterPayeursDistincts(lignesExercice);
    const cotisationsAcquittees = sommeExacte(payeuses.map((l) => l.montant));
    const appels = lignesExercice.length;
    return {
        annee,
        payeurs,
        cotisationsAcquittees,
        produitMoyen: payeurs > 0 ? cotisationsAcquittees / payeurs : 0,
        appels,
        tauxRecouvrement: appels > 0 ? payeurs / appels : 0,
        doublonsEcartes,
        cotisationSolidaire: COTISATION_SOLIDAIRE[annee] ?? 0,
    };
}
/** Séries de tous les exercices présents dans le jeu, triées par millésime. */
export function series(lignes) {
    const parAnnee = new Map();
    for (const l of lignes) {
        const bucket = parAnnee.get(l.annee);
        if (bucket)
            bucket.push(l);
        else
            parAnnee.set(l.annee, [l]);
    }
    return [...parAnnee.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([annee, ls]) => serieExercice(annee, ls));
}
/**
 * Effectif projeté de l'exercice en cours : le taux de recouvrement de
 * l'exercice précédent appliqué au nombre d'appels déjà émis, arrondi.
 */
export function payeursProjetes(appelsExerciceCourant, tauxExercicePrecedent) {
    return Math.round(appelsExerciceCourant * tauxExercicePrecedent);
}
/** Effectifs projetés par interpolation linéaire vers la cible 2030, arrondis. */
export function effectifsInterpoles(effectifDepart, cible, nbPas) {
    const out = [effectifDepart];
    for (let i = 1; i <= nbPas; i++) {
        out.push(Math.round(effectifDepart + ((cible - effectifDepart) * i) / nbPas));
    }
    return out;
}
/** Ventilation des appels de l'exercice par collège. */
export function ventilationCollege(lignesExercice) {
    const m = new Map();
    for (const l of lignesExercice) {
        const c = l.college ?? 'Non renseigné';
        const e = m.get(c) ?? { appels: 0, payeurs: 0 };
        e.appels += 1;
        if (estPayeur(l))
            e.payeurs += 1;
        m.set(c, e);
    }
    return [...m.entries()]
        .map(([college, v]) => ({ college, ...v }))
        .sort((a, b) => b.appels - a.appels);
}
/** Ventilation des appels de l'exercice par type de cotisant. */
export function ventilationTypeCotisant(lignesExercice) {
    const m = new Map();
    for (const l of lignesExercice) {
        const t = l.typeCotisant ?? 'Non renseigné';
        const e = m.get(t) ?? { appels: 0, payeurs: 0 };
        e.appels += 1;
        if (estPayeur(l))
            e.payeurs += 1;
        m.set(t, e);
    }
    return [...m.entries()]
        .map(([type, v]) => ({ type, ...v }))
        .sort((a, b) => b.appels - a.appels);
}
//# sourceMappingURL=adherents.js.map